<script lang="ts">
import { tv } from 'tailwind-variants';

const theme = tv({
	slots: {
		root: 'overflow-clip ease-out',
		sheet: '',
	},
	variants: {
		dimension: {
			width: {
				root: 'w-(--sheet-width) motion-safe:transition-[width]',
				sheet: 'w-max',
			},
			height: {
				root: 'h-(--sheet-height) motion-safe:transition-[height]',
				sheet: 'h-max',
			},
			both: {
				root: 'h-(--sheet-height) w-(--sheet-width) motion-safe:transition-[width,height]',
				sheet: 'size-max',
			},
		},
	},
});
</script>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core';
import { computed, useTemplateRef } from 'vue';

defineOptions({ inheritAttrs: false });

const {
	dimension = 'both',
	duration = 50,
	maintainMaxSize,
	ui: uiProp,
	class: className,
} = defineProps<{
	class?: string;
	dimension?: 'width' | 'height' | 'both';
	/**
	 * Maintains the maximum reached size of the sheet along this dimension, even if the content changes.
	 */
	maintainMaxSize?: 'width' | 'height' | 'both';
	duration?: number;
	ui?: Partial<typeof theme['slots']>;
}>();
const wrapperRef = useTemplateRef('wrapper');
const sizerRef = useTemplateRef('sizer');

const ui = computed(() => theme({ dimension }));

let maxHeight = 0;
let maxWidth = 0;
let renderedHeight = '';
let renderedWidth = '';

useResizeObserver(sizerRef, () => {
	const tracksHeight = dimension !== 'width'
		|| maintainMaxSize === 'height'
		|| maintainMaxSize === 'both';
	const tracksWidth = dimension !== 'height'
		|| maintainMaxSize === 'width'
		|| maintainMaxSize === 'both';

	if (tracksHeight) {
		const height = Math.max(sizerRef.value!.offsetHeight, maxHeight);
		const value = `${height.toFixed(1)}px`;

		if (value !== renderedHeight) {
			wrapperRef.value!.style.setProperty('--sheet-height', value);
			renderedHeight = value;
		}

		if (maintainMaxSize === 'height' || maintainMaxSize === 'both') maxHeight = height;
	}

	if (tracksWidth) {
		const width = Math.max(sizerRef.value!.offsetWidth, maxWidth);
		const value = `${width.toFixed(1)}px`;

		if (value !== renderedWidth) {
			wrapperRef.value!.style.setProperty('--sheet-width', value);
			renderedWidth = value;
		}

		if (maintainMaxSize === 'width' || maintainMaxSize === 'both') maxWidth = width;
	}
});
</script>

<template>
	<div
		ref="wrapper"
		:class="ui.root({ class: uiProp?.root })"
		:style="{ transitionDuration: `${duration}ms` }"
	>
		<div
			ref="sizer"
			:class="ui.sheet({ class: [uiProp?.sheet, className] })"
			v-bind="$attrs"
		>
			<slot />
		</div>
	</div>
</template>
